import { FOCO_VISIVEL } from 'utils/foco';

import { CartaoDeExperiencia as Cartao, FiltroDoCatalogo } from '../catalogo-modelo';
import CartaoDeExperiencia from './CartaoDeExperiencia';

const FILTROS: { chave: FiltroDoCatalogo; rotulo: string }[] = [
  { chave: 'todas', rotulo: 'Todas' },
  { chave: 'unico', rotulo: 'Único' },
  { chave: 'combo', rotulo: 'Combo' },
];

interface ListaDeExperienciasProps {
  cartoes: Cartao[];
  filtro: FiltroDoCatalogo;
  onFiltro: (filtro: FiltroDoCatalogo) => void;
  viradoId?: string;
  onVirar: (id?: string) => void;
  onConfirmar: (cartao: Cartao) => void;
  onPromocoesLivelo: () => void;
  /** Período encerrado: cartões só com as regras e o rodapé sem a frase da troca (TRC-20, TRC-22). */
  somenteConsulta?: boolean;
}

/** "Escolha a sua experiência", contagem, filtros, grade e rodapé (CAT-07, CAT-08, CAT-11). */
export default function ListaDeExperiencias({
  cartoes,
  filtro,
  onFiltro,
  viradoId,
  onVirar,
  onConfirmar,
  onPromocoesLivelo,
  somenteConsulta = false,
}: ListaDeExperienciasProps) {
  return (
    <section aria-labelledby="titulo-lista-experiencias">
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-5">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <h2
            id="titulo-lista-experiencias"
            tabIndex={0}
            className={`m-0 font-display text-[19px] font-bold leading-[normal] text-brand-blue-dark ${FOCO_VISIVEL}`}
          >
            Escolha a sua experiência
          </h2>
          {/* SPEC_DEVIATION: número em `brand-blue-dark` e texto em `content` (design 4,35 e 4,43:1 sobre o cinza da página). */}
          <span aria-live="polite" className="text-[12px] text-content">
            <b className="text-brand-blue-dark">{cartoes.length}</b> experiências disponíveis
          </span>
        </div>
        <div role="group" aria-label="Filtrar experiências por tipo" className="flex items-center gap-2">
          {FILTROS.map(({ chave, rotulo }) => (
            <button
              key={chave}
              type="button"
              aria-pressed={filtro === chave}
              onClick={() => onFiltro(chave)}
              className={`cursor-pointer rounded-lg border border-solid px-[15px] py-2 font-text text-[12px] font-bold transition-colors ${
                filtro === chave
                  ? 'border-brand-blue bg-brand-blue text-brand-white'
                  : 'border-surface-200 bg-brand-white text-content-soft hover:border-brand-blue'
              } ${FOCO_VISIVEL}`}
            >
              {rotulo}
            </button>
          ))}
        </div>
      </div>

      {cartoes.length > 0 ? (
        // As colunas acompanham a largura do CONTEÚDO, não da tela: com o menu lateral aberto, 1024px
        // de tela deixam ~600px de conteúdo, e três colunas espremiam os cartões. Com mínimo de 260px,
        // os 1000px do design dão as 3 colunas de 318px dele.
        <ul aria-labelledby="titulo-lista-experiencias" className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-[22px] p-0">
          {cartoes.map(cartao => (
            <li key={cartao.id}>
              <CartaoDeExperiencia
                cartao={cartao}
                virado={viradoId === cartao.id}
                onVirar={() => onVirar(cartao.id)}
                onDesvirar={() => onVirar(undefined)}
                onConfirmar={() => onConfirmar(cartao)}
                onPromocoesLivelo={onPromocoesLivelo}
                somenteConsulta={somenteConsulta}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p tabIndex={0} className={`m-0 rounded-xl bg-brand-white px-6 py-5 text-[13px] text-content shadow-jj-01 ${FOCO_VISIVEL}`}>
          Nenhuma experiência deste tipo neste ciclo.
        </p>
      )}

      {/* SPEC_DEVIATION: cor `content` (design `text-soft`, 4,43:1 sobre o cinza da página). */}
      <p tabIndex={0} className={`m-0 mt-[22px] text-[11px] leading-[1.6] text-content ${FOCO_VISIVEL}`}>
        Experiências sujeitas à disponibilidade de vagas.{!somenteConsulta && ' A escolha pode ser alterada até o encerramento do prazo.'}
      </p>
    </section>
  );
}
